"use client";

// CatalogPage como ilha client. Recebe dados iniciais (parts, categorias,
// fabricantes, marcas, series, t, lang) via props vindo do wrapper Server
// Component. State e fetch derivados vivem aqui.

import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import { useSiteData } from "../lib/site-context";
import {
  buildProductSlug,
  displayRef,
  pickKind,
} from "../lib/mbm-helpers";
import { withLocale } from "../lib/i18n";
import { mapToPtPath } from "../lib/route-map";
import { truncate } from "../lib/text";
import { useLangContext } from "../lib/lang-context";
import { ProductMedia, ProductSearchBar } from "./mbm-components";

const COLLAPSE_AFTER = 10;

function FacetGroup({ label, options, value, onPick }) {
  const visible = options.slice(0, COLLAPSE_AFTER);
  const overflow = options.slice(COLLAPSE_AFTER);

  return (
    <div className="cat-controls" style={{ marginBottom: 8 }}>
      <div className="group" style={{ flexWrap: "wrap" }}>
        <span className="group-label">{label}</span>
        {visible.map((opt) => (
          <button
            key={opt.slug}
            className={"chip " + (value === opt.slug ? "on" : "")}
            onClick={() => onPick(opt.slug)}
          >
            {opt.nome}
          </button>
        ))}
        {overflow.length > 0 && (
          <details style={{ display: "inline-block" }}>
            <summary className="chip" style={{ cursor: "pointer", listStyle: "none" }}>
              Mais {overflow.length}
            </summary>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {overflow.map((opt) => (
                <button
                  key={opt.slug}
                  className={"chip " + (value === opt.slug ? "on" : "")}
                  onClick={() => onPick(opt.slug)}
                >
                  {opt.nome}
                </button>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function CatalogPartnerNitram() {
  // Empresa parceira vinda do CMS (footer.partner*), editável no CRM.
  const { footer } = useSiteData();
  const name = footer?.partnerName;
  if (!name) return null;

  const heading = footer?.partnerCardHeading || "";
  // Realça o nome do parceiro dentro do heading (mantém o <strong> antigo).
  const segs = heading && name ? heading.split(name) : null;

  return (
    <aside className="catalog-partner-card" role="complementary">
      {footer?.partnerLabel && (
        <div className="catalog-partner-eyebrow">{footer.partnerLabel}</div>
      )}
      {heading && (
        <h3 className="catalog-partner-title">
          {segs && segs.length > 1
            ? segs.map((seg, i) => (
                <Fragment key={i}>
                  {seg}
                  {i < segs.length - 1 && <strong>{name}</strong>}
                </Fragment>
              ))
            : heading}
        </h3>
      )}
      {footer?.partnerCardDescription && (
        <p className="catalog-partner-desc">{footer.partnerCardDescription}</p>
      )}
      {footer?.partnerUrl && (
        <a
          className="cta-btn catalog-partner-cta"
          href={footer.partnerUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {footer?.partnerCardCtaLabel || "Visitar"} <span className="arr" aria-hidden="true">→</span>
        </a>
      )}
    </aside>
  );
}

function CatalogActiveFilters({
  fCategoria, fFabricante, fMarca, fSerie,
  categorias, fabricantes, marcas, series,
  onClear, onClearAll, t,
}) {
  const lookup = (list, slug) => {
    if (!slug) return "";
    const arr = Array.isArray(list) ? list : [];
    const found = arr.find((x) => (x.slug ?? x.id) === slug);
    return found?.nome ?? found?.name ?? slug;
  };

  const active = [
    fCategoria && { key: "categoria", label: t("mega_col_category") || "Categoria", value: lookup(categorias, fCategoria) },
    fFabricante && { key: "fabricante", label: t("mega_col_manufacturer") || "Fabricante", value: lookup(fabricantes, fFabricante) },
    fMarca && { key: "marca", label: t("mega_col_brand") || "Marca", value: lookup(marcas, fMarca) },
    fSerie && { key: "serie", label: t("mega_col_series") || "Série", value: lookup(series, fSerie) },
  ].filter(Boolean);

  if (active.length === 0) return null;

  return (
    <div className="catalog-active-filters" role="region" aria-label={t("active_filters") || "Filtros ativos"}>
      <span className="catalog-active-filters-label">
        {t("filtered_by") || "Filtrado por"}:
      </span>
      {active.map((f) => (
        <button
          key={f.key}
          type="button"
          className="catalog-active-chip"
          onClick={() => onClear(f.key, "")}
          aria-label={`${t("remove_filter") || "Remover filtro"}: ${f.label} ${f.value}`}
        >
          <span className="catalog-active-chip-label">{f.label}:</span>
          <span className="catalog-active-chip-value">{f.value}</span>
          <span className="catalog-active-chip-x" aria-hidden="true">×</span>
        </button>
      ))}
      <button
        type="button"
        className="catalog-active-clear"
        onClick={onClearAll}
      >
        {t("clear_all") || "Limpar tudo"}
      </button>
    </div>
  );
}

function CatalogEmptyState({ t, q, lang }) {
  const message = q ? t("catalog_empty_search") : t("catalog_empty_default");
  const targetPath = q
    ? `/contact?searchQuery=${encodeURIComponent(q)}`
    : "/contact";
  return (
    <div
      style={{
        border: "1px solid var(--rule)",
        padding: 48,
        textAlign: "center",
        maxWidth: 640,
        margin: "32px auto",
      }}
    >
      <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 17 }}>
        {message}
        {q ? ` "${q}".` : ""}
      </p>
      <div style={{ marginTop: 24 }}>
        <Link className="cta-btn" href={withLocale(mapToPtPath(targetPath), lang)}>
          {t("catalog_empty_cta")} <span className="arr" aria-hidden="true">→</span>
        </Link>
      </div>
    </div>
  );
}

export default function CatalogPage({
  parts,
  categorias,
  fabricantes,
  marcas,
  series,
  lang,
}) {
  const { t } = useLangContext();
  const initialParts = useMemo(() => (Array.isArray(parts) ? parts : []), [parts]);

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname() ?? "/pecas";
  const q = searchParams?.get("q") ?? "";
  const fCategoria = searchParams?.get("categoria") ?? "";
  const fFabricante = searchParams?.get("fabricante") ?? "";
  const fMarca = searchParams?.get("marca") ?? "";
  const fSerie = searchParams?.get("serie") ?? "";

  const filterKey = `${q}|${fCategoria}|${fFabricante}|${fMarca}|${fSerie}`;
  const hasAnyFilter = filterKey !== "||||";

  const setFilter = (key, value) => {
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    const current = params.get(key) ?? "";
    if (!value || value === current) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    const qs = params.toString();
    router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  };

  const clearAllFilters = () => {
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    ["q", "categoria", "fabricante", "marca", "serie"].forEach((k) => params.delete(k));
    const qs = params.toString();
    router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  };

  const navigate = (path) => {
    router.push(withLocale(mapToPtPath(path), lang));
  };

  const [remote, setRemote] = useState({
    key: null,
    list: [],
    total: 0,
    errored: false,
  });

  useEffect(() => {
    if (!hasAnyFilter) return undefined;

    let cancelled = false;

    const params = new URLSearchParams({ size: "100" });
    if (q) params.set("q", q);
    if (fCategoria) params.set("categoriaSlug", fCategoria);
    if (fFabricante) params.set("fabricanteSlug", fFabricante);
    if (fMarca) params.set("marcaSlug", fMarca);
    if (fSerie) params.set("serieSlug", fSerie);

    params.set("lang", lang);
    fetch(`/api/products?${params.toString()}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.content) ? data.content : [];
        const total =
          typeof data?.totalElements === "number" ? data.totalElements : list.length;
        setRemote({ key: filterKey, list, total, errored: false });
      })
      .catch(() => {
        if (cancelled) return;
        setRemote({ key: filterKey, list: [], total: 0, errored: true });
      });

    return () => {
      cancelled = true;
    };
  }, [filterKey, lang, q, fCategoria, fFabricante, fMarca, fSerie, hasAnyFilter]);

  const loading = hasAnyFilter && remote.key !== filterKey && !remote.errored;
  const errored = hasAnyFilter && remote.key === filterKey && remote.errored;
  const results =
    hasAnyFilter && remote.key === filterKey
      ? remote.list
      : hasAnyFilter
      ? []
      : initialParts;
  const total =
    hasAnyFilter && remote.key === filterKey
      ? remote.total
      : hasAnyFilter
      ? 0
      : initialParts.length;
  const showEmptyState = !loading && !errored && results.length === 0;

  return (
    <div className="page catalog-page">
      <section className="hero shell catalog-hero">
        <div className="catalog-search-wrap">
          <ProductSearchBar t={t} navigate={navigate} initialValue={q} />
        </div>
        <h1 className="catalog-title">
          {t("sec_catalog_t_a")} {t("sec_catalog_t_b")}
        </h1>
      </section>

      <section className="section tight shell">
        <h2 className="visually-hidden">{t("nav.catalog") || t("sec_catalog_t_a") || "Produtos"}</h2>

        {fCategoria === "maquinas-usadas" && <CatalogPartnerNitram />}

        <CatalogActiveFilters
          fCategoria={fCategoria}
          fFabricante={fFabricante}
          fMarca={fMarca}
          fSerie={fSerie}
          categorias={categorias}
          fabricantes={fabricantes}
          marcas={marcas}
          series={series}
          onClear={setFilter}
          onClearAll={clearAllFilters}
          t={t}
        />

        <div className="catalog-count">
          {loading
            ? t("loading")
            : q
            ? `${t("search_results_for")} "${q}" — ${total} ${t("items")}`
            : `${total} ${t("items")}`}
        </div>

        {showEmptyState ? (
          <CatalogEmptyState t={t} q={q} lang={lang} />
        ) : (
          <div className="cards-grid" style={{ opacity: loading ? 0.5 : 1, transition: "opacity .15s" }}>
            {results.map((p, i) => {
              const meta = [displayRef(p.ref), p.category[0]].filter(Boolean).join(" · ");
              return (
                <Link
                  key={p.id ?? p.ref}
                  href={withLocale(`/produto/${buildProductSlug(p)}`, lang)}
                  className="cat-card cat-card-link"
                >
                  <ProductMedia product={p} kind={pickKind(p.type)} priority={i === 0} />
                  <div className="body">
                    {meta && <div className="ref">{meta}</div>}
                    <h3>{p.name[lang]}</h3>
                    {p.desc[lang] && <p>{truncate(p.desc[lang])}</p>}
                    {p.specs.length > 0 && (
                      <div className="specs">{p.specs.map((s, i) => <span key={i}>{s}</span>)}</div>
                    )}
                    <span className="cat-card-cta">
                      {t("learn_more")} <span className="arr" aria-hidden="true">→</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
